import React from 'react';
import { Button } from '@edusense/shared-ui-kit';
import { ProjectForm } from '@edusense/shared-ui-project-forms';
import { FileTree } from '@edusense/shared-ui-file-tree';
import { MediaUploader } from '@edusense/shared-ui-media-upload';
import { FolderUploader } from '@edusense/shared-ui-folder-upload';
import { FeedbackMessage } from '@edusense/shared-ui-content';
import X from 'lucide-react/dist/esm/icons/x';
import Archive from 'lucide-react/dist/esm/icons/archive';
import { useProjectUpload } from '../hooks/useProjectUpload';
import { PROJECT_UPLOAD_TEST_IDS } from '@edusense/shared-constants';
/**
 * 프로젝트 업로드/수정 페이지 컴포넌트
 * 🏗️ FSD Features Layer - Legacy 717라인 → FSD 아키텍처로 완전 리팩토링
 */ var ProjectUploadPage = function(param) {
    var projectId = param.projectId, _param_mode = param.mode, mode = _param_mode === void 0 ? 'create' : _param_mode;
    var _useProjectUpload = useProjectUpload({
        projectId: projectId,
        mode: mode
    }), formData = _useProjectUpload.formData, setFormData = _useProjectUpload.setFormData, isSubmitting = _useProjectUpload.isSubmitting, media = _useProjectUpload.media, mediaUploadStatus = _useProjectUpload.mediaUploadStatus, mediaUploadError = _useProjectUpload.mediaUploadError, triggerMediaUpload = _useProjectUpload.triggerMediaUpload, handleMediaUpload = _useProjectUpload.handleMediaUpload, handleRemoveMediaAdapter = _useProjectUpload.handleRemoveMediaAdapter, mediaInputRef = _useProjectUpload.mediaInputRef, fileTree = _useProjectUpload.fileTree, projectFiles = _useProjectUpload.projectFiles, fileUploadStatus = _useProjectUpload.fileUploadStatus, fileUploadProgress = _useProjectUpload.fileUploadProgress, uploadError = _useProjectUpload.uploadError, triggerFolderUpload = _useProjectUpload.triggerFolderUpload, handleFolderUpload = _useProjectUpload.handleFolderUpload, folderInputRef = _useProjectUpload.folderInputRef, handleDropFolder = _useProjectUpload.handleDropFolder, uploadedZipFile = _useProjectUpload.uploadedZipFile, zipInputRef = _useProjectUpload.zipInputRef, handleZipFileChange = _useProjectUpload.handleZipFileChange, triggerZipUpload = _useProjectUpload.triggerZipUpload, clearZipUpload = _useProjectUpload.clearZipUpload, validationFeedback = _useProjectUpload.validationFeedback, handleGoBack = _useProjectUpload.handleGoBack, handleSubmit = _useProjectUpload.handleSubmit;
    var folderUploadStatus = React.useMemo(function() {
        if (uploadError) {
            return 'error';
        }
        if (fileUploadStatus === 'uploading') {
            return 'uploading';
        }
        if (uploadedZipFile || projectFiles && projectFiles.length > 0) {
            return 'success';
        }
        return 'idle';
    }, [
        uploadError,
        fileUploadStatus,
        uploadedZipFile,
        projectFiles
    ]);
    var isSubmitDisabled = React.useMemo(function() {
        var hasProjectFiles = Boolean(uploadedZipFile) || fileTree && projectFiles.length > 0;
        return isSubmitting || !hasProjectFiles;
    }, [
        isSubmitting,
        uploadedZipFile,
        fileTree,
        projectFiles.length
    ]);
    var formatFileSize = React.useCallback(function(size) {
        if (!size) return '0 MB';
        return "".concat((size / 1024 / 1024).toFixed(2), " MB");
    }, []);
    return /*#__PURE__*/ React.createElement("form", {
        className: "container mx-auto p-6 space-y-6",
        onSubmit: handleSubmit,
        noValidate: true,
        "data-testid": PROJECT_UPLOAD_TEST_IDS.PAGE_FORM
    }, /*#__PURE__*/ React.createElement("input", {
        ref: mediaInputRef,
        className: "hidden",
        type: "file",
        multiple: true,
        accept: "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm",
        onChange: handleMediaUpload
    }), /*#__PURE__*/ React.createElement("input", {
        ref: zipInputRef,
        className: "hidden",
        type: "file",
        accept: ".zip,application/zip,application/x-zip-compressed",
        onChange: handleZipFileChange,
        disabled: Boolean(projectFiles.length)
    }), /*#__PURE__*/ React.createElement("h1", {
        className: "text-2xl font-bold",
        "data-testid": PROJECT_UPLOAD_TEST_IDS.PAGE_TITLE
    }, mode === 'edit' ? '프로젝트 수정' : '새 프로젝트 업로드'), /*#__PURE__*/ React.createElement("section", {
        "data-testid": PROJECT_UPLOAD_TEST_IDS.INFO_SECTION
    }, /*#__PURE__*/ React.createElement("h2", {
        className: "text-lg font-medium mb-4"
    }, "프로젝트 정보"), /*#__PURE__*/ React.createElement(ProjectForm, {
        values: formData,
        onChange: setFormData,
        isSubmitting: isSubmitting
    })), /*#__PURE__*/ React.createElement("section", {
        className: "space-y-4",
        "data-testid": PROJECT_UPLOAD_TEST_IDS.FILE_SECTION
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex items-center justify-between"
    }, /*#__PURE__*/ React.createElement("h2", {
        className: "text-lg font-medium"
    }, "파일 업로드"), /*#__PURE__*/ React.createElement("div", {
        className: "flex gap-2"
    }, /*#__PURE__*/ React.createElement(Button, {
        type: "button",
        variant: uploadedZipFile ? 'outline' : 'primary',
        onClick: triggerFolderUpload,
        disabled: Boolean(uploadedZipFile) || isSubmitting,
        "data-testid": PROJECT_UPLOAD_TEST_IDS.FOLDER_UPLOAD_BUTTON
    }, "폴더 업로드"), /*#__PURE__*/ React.createElement(Button, {
        type: "button",
        variant: uploadedZipFile ? 'primary' : 'outline',
        onClick: triggerZipUpload,
        disabled: Boolean(projectFiles.length) || isSubmitting,
        leftIcon: /*#__PURE__*/ React.createElement(Archive, {
            className: "h-4 w-4"
        }),
        "data-testid": PROJECT_UPLOAD_TEST_IDS.ZIP_UPLOAD_BUTTON
    }, "ZIP 파일 선택"))), !uploadedZipFile && /*#__PURE__*/ React.createElement(FolderUploader, {
        uploadStatus: folderUploadStatus,
        uploadProgress: fileUploadProgress,
        uploadError: uploadError,
        onUpload: triggerFolderUpload,
        onFolderChange: handleFolderUpload,
        folderInputRef: folderInputRef,
        onDropFolder: handleDropFolder
    }), uploadedZipFile && /*#__PURE__*/ React.createElement("div", {
        className: "flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 p-4",
        "data-testid": PROJECT_UPLOAD_TEST_IDS.ZIP_SUMMARY
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex items-center gap-3"
    }, /*#__PURE__*/ React.createElement(Archive, {
        className: "h-5 w-5 text-gray-500"
    }), /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("p", {
        className: "text-sm font-medium text-gray-900"
    }, uploadedZipFile.name), /*#__PURE__*/ React.createElement("p", {
        className: "text-xs text-gray-500"
    }, formatFileSize(uploadedZipFile.size)))), /*#__PURE__*/ React.createElement(Button, {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: clearZipUpload,
        leftIcon: /*#__PURE__*/ React.createElement(X, {
            className: "h-4 w-4"
        })
    }, "선택 해제")), validationFeedback.message && /*#__PURE__*/ React.createElement(FeedbackMessage, {
        message: validationFeedback.message,
        type: validationFeedback.type,
        "data-testid": PROJECT_UPLOAD_TEST_IDS.VALIDATION_MESSAGE
    }), uploadError && !validationFeedback.message && /*#__PURE__*/ React.createElement(FeedbackMessage, {
        message: uploadError,
        type: "error",
        "data-testid": PROJECT_UPLOAD_TEST_IDS.VALIDATION_MESSAGE
    }), fileTree && /*#__PURE__*/ React.createElement("div", {
        className: "rounded-md border border-gray-200 p-4",
        "data-testid": PROJECT_UPLOAD_TEST_IDS.FILE_TREE
    }, /*#__PURE__*/ React.createElement("h3", {
        className: "mb-3 text-sm font-medium text-gray-700"
    }, uploadedZipFile ? 'ZIP 파일 구조' : '프로젝트 파일 구조'), /*#__PURE__*/ React.createElement(FileTree, {
        fileTree: fileTree
    }))), /*#__PURE__*/ React.createElement("section", {
        "data-testid": PROJECT_UPLOAD_TEST_IDS.MEDIA_SECTION
    }, /*#__PURE__*/ React.createElement("h2", {
        className: "text-lg font-medium mb-4"
    }, "미디어"), /*#__PURE__*/ React.createElement(MediaUploader, {
        media: media,
        uploadStatus: mediaUploadStatus,
        uploadError: mediaUploadError,
        onUpload: triggerMediaUpload,
        onRemove: handleRemoveMediaAdapter,
        testIds: {
            addButton: PROJECT_UPLOAD_TEST_IDS.MEDIA_ADD_BUTTON,
            mediaItem: PROJECT_UPLOAD_TEST_IDS.MEDIA_ITEM
        }
    })), /*#__PURE__*/ React.createElement("div", {
        className: "flex justify-end gap-4"
    }, /*#__PURE__*/ React.createElement(Button, {
        type: "button",
        variant: "outline",
        onClick: handleGoBack,
        disabled: isSubmitting,
        "data-testid": PROJECT_UPLOAD_TEST_IDS.CANCEL_BUTTON
    }, "취소"), /*#__PURE__*/ React.createElement(Button, {
        type: "submit",
        variant: "primary",
        disabled: isSubmitDisabled,
        "data-testid": PROJECT_UPLOAD_TEST_IDS.SUBMIT_BUTTON
    }, mode === 'edit' ? '수정' : '업로드')));
};
// Named export for consistency with index.ts
export { ProjectUploadPage };
// Default export for backward compatibility  
export default ProjectUploadPage;
